import { STAP_VALIDEREN, STAP_VASTLEGGEN } from './kerncijfers';
import type { HandlingTimeBucket, HandlingTimePerPersonRow } from './handlingTime.api';

/**
 * Story 33.1 — het rekenwerk onder het stapfilter.
 *
 * Hier staat uitsluitend rekenwerk en geen opmaak, om dezelfde reden als bij `kerncijfers.ts` en
 * `tabel.ts`: de scherpste regel van deze story is een rekenregel — wie valt er binnen de gekozen
 * stap en wie niet — en die hoort op één navraagbare plek te staan in plaats van verspreid over de
 * drie onderdelen die er elk hun eigen versie van zouden maken.
 *
 * > [!warning] Filteren is WEGLATEN, niet doorstrepen
 * > Dit bestand snijdt de andere stap uit de gegevens weg vóór er iets gerekend wordt. Daardoor
 * > hoeven de kerncijfers, de tabel en de grafiek niet elk apart te weten dat er gefilterd is: ze
 * > rekenen gewoon door op wat ze krijgen, en dat is precies één stap. Zouden ze de andere stap wél
 * > binnenkrijgen en zelf verbergen, dan is elke plek die dat vergeet een getal dat over twee
 * > stappen gaat terwijl het scherm er één belooft (AC-2 en AC-3).
 *
 * Wat het NIET doet: iemand met een leeg vakje laten staan. Wie in de gekozen stap geen minuut
 * droeg valt eruit — en komt daarom terug in `weggevallen`, want een naam die zonder een woord van
 * het scherm verdwijnt is erger dan een naam met een streepje (AC-6 en AC-7).
 */

/** De drie standen, in de volgorde waarin ze op het scherm staan. */
export const STAPFILTERS = ['vastleggen', 'valideren', 'beide'] as const;
export type StapFilter = (typeof STAPFILTERS)[number];

/** De twee echte stappen: `beide` is een stand van het filter, geen stap in de gegevens. */
export type StapKeuze = Exclude<StapFilter, 'beide'>;

/**
 * De beginstand.
 *
 * Vastleggen, en dat is dezelfde beginstand die de positiekolom sinds story 31.6 had: 92% van de
 * gemeten tijd valt in die stap, dus dat is waar een lezer op uitkomt zonder iets te kiezen (AC-5).
 */
export const STANDAARD_STAPFILTER: StapFilter = 'vastleggen';

/** De naam van de parameter in het webadres. Eén parameter, drie waarden (AC-12). */
export const STAP_PARAMETER = 'stap';

/** Van de woorden op het scherm naar de woorden van de bron. */
const BRONNAAM: Record<StapKeuze, string> = {
  vastleggen: STAP_VASTLEGGEN,
  valideren: STAP_VALIDEREN,
};

/**
 * Wat er in het adres staat, terug naar een stand.
 *
 * Alles wat geen van de drie is valt terug op de beginstand — zonder foutmelding, want een typefout
 * in een gedeelde link is geen storing van dit scherm (AC-13).
 */
export function leesStapFilter(waarde: string | null | undefined): StapFilter {
  return STAPFILTERS.includes(waarde as StapFilter) ? (waarde as StapFilter) : STANDAARD_STAPFILTER;
}

/**
 * De stap waarop de positiekolom rangschikt.
 *
 * Op `beide` is dat vastleggen, zoals vandaag — de nummering moet op één stap slaan, want de twee
 * zijn niet bij elkaar op te tellen (AC-10).
 */
export function stapVoorRangschikking(filter: StapFilter): StapKeuze {
  return filter === 'beide' ? 'vastleggen' : filter;
}

/**
 * Of dit antwoord de uitsplitsing per stap MIST — al is het maar op één regel.
 *
 * Dat kan tijdens een uitrol gebeuren: een serverlaag van vóór story 31.1 stuurt regels zonder
 * `handlings`. Filteren zou zo'n regel wegstrepen, en het scherm zou dan bij naam beweren dat die
 * collega in deze stap niets deed — terwijl er over hem alleen niets uitgesplitst is. Dat is een
 * onwaarheid over een persoon, en precies het soort stille verdwijning dat story 33.1 wegneemt. Het
 * filter gaat daarom op slot en zegt waarom.
 *
 * ÉÉN REGEL IS GENOEG, en dat is een codereviewbevinding van 30 augustus 2026. Dit stond hier als
 * "állemaal zonder uitsplitsing", en een gemengd antwoord glipte er dus doorheen: de regels zónder
 * uitsplitsing kwamen bij de weggevallenen terecht, met een reden die niet klopte.
 *
 * Een LEEG antwoord telt hier NIET als ontbrekende uitsplitsing: er is dan geen enkele regel om iets
 * uit af te leiden, en een periode zonder metingen hoort het gewone lege scherm te houden.
 */
export function uitsplitsingOntbreekt(rijen: HandlingTimePerPersonRow[]): boolean {
  return rijen.some((rij) => rij.handlings === undefined);
}

/** De minuten van één stap op één regel; `null` — nooit `0` — zodra er niets gemeten is. */
function minutenVoorStap(rij: HandlingTimePerPersonRow, bron: string): number | null {
  let minuten: number | null = null;
  for (const eigen of rij.handlings ?? []) {
    if (eigen.handling !== bron || eigen.minutes === null) continue;
    minuten = (minuten ?? 0) + eigen.minutes;
  }
  return minuten;
}

export interface GefilterdeRijen {
  /** De regels zoals het scherm ze toont, met uitsluitend de gekozen stap erin. */
  getoond: HandlingTimePerPersonRow[];
  /** Wie door het filter buiten beeld valt. Onbewerkt, zodat het scherm de naam kan opzoeken. */
  weggevallen: HandlingTimePerPersonRow[];
}

/**
 * De regels beperkt tot één stap, en de namen van wie daardoor wegvalt.
 *
 * Op `beide` gebeurt er niets: dan toont het scherm wat het vandaag toont (AC-4).
 */
export function beperkRijenTotStap(rijen: HandlingTimePerPersonRow[], filter: StapFilter): GefilterdeRijen {
  if (filter === 'beide') return { getoond: rijen, weggevallen: [] };

  const bron = BRONNAAM[filter];
  const getoond: HandlingTimePerPersonRow[] = [];
  const weggevallen: HandlingTimePerPersonRow[] = [];

  for (const rij of rijen) {
    // GEEN MINUUT IN DEZE STAP is het criterium, en niet "geen gemiddelde". Wie wél minuten droeg
    // maar geen behandelingen heeft een streepje in de laatste kolom en hoort gewoon in beeld: hij
    // droeg tijd, en dat is precies waar dit scherm over gaat (AC-7).
    if (minutenVoorStap(rij, bron) === null) {
      weggevallen.push(rij);
      continue;
    }
    getoond.push({ ...rij, handlings: (rij.handlings ?? []).filter((eigen) => eigen.handling === bron) });
  }

  return { getoond, weggevallen };
}

/**
 * Dezelfde snede op de reeks over de tijd.
 *
 * Een tijdvak waarin alleen de ándere stap gemeten is houdt hier een LEGE lijst handelingen over,
 * en `bouwReeks` maakt daar dan `gemeten: false` van — geen staaf, en wél het merkteken "niet
 * gemeten". Dat is precies goed: op deze stap is er in dat tijdvak niets gebeurd, en stil weglaten
 * zou lezen alsof die dag niet bestond.
 */
export function beperkBucketsTotStap(
  buckets: HandlingTimeBucket[] | undefined,
  filter: StapFilter,
): HandlingTimeBucket[] | undefined {
  if (filter === 'beide' || buckets === undefined) return buckets;
  const bron = BRONNAAM[filter];
  return buckets.map((bucket) => ({ ...bucket, handlings: bucket.handlings.filter((eigen) => eigen.handling === bron) }));
}
